import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Dashboard.css';
import { useTranslation } from 'react-i18next';

interface Project {
  id: number;
  attributes: {
    name: string;
    description?: string;
    status: 'planning' | 'in_progress' | 'completed' | 'paused';
    location?: string;
    client?: string;
    startDate?: string;
    endDate?: string;
    image?: {
      data?: {
        attributes: {
          url: string;
        };
      };
    };
    alerts?: {
      data: any[];
    };
    workReports?: {
      data: any[];
    };
  };
}

interface DashboardStats {
  totalProjects: number;
  activeProjects: number;
  totalDamages: number;
  completedProjects: number;
}

export const Dashboard = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalProjects: 0,
    activeProjects: 0,
    totalDamages: 0,
    completedProjects: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchProjects();
    fetchStats();
  }, []);

  const fetchProjects = async () => {
    try {
      const backendUrl = sessionStorage.getItem('backendUrl') || 'http://localhost:1337';
      const response = await fetch(`${backendUrl}/api/projects?populate=*`);
      const data = await response.json();
      setProjects(data.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const backendUrl = sessionStorage.getItem('backendUrl') || 'http://localhost:1337';
      
      // Fetch projects
      const projectsResponse = await fetch(`${backendUrl}/api/projects?populate=*`);
      const projectsData = await projectsResponse.json();
      const projectsList = projectsData.data || [];

      const alertsResponse = await fetch(
        `${backendUrl}/api/alerts?filters[roadDamageNumber][$notNull]=true&filters[project][id][$notNull]=true&pagination[pageSize]=1`
      );
      const alertsData = await alertsResponse.json();

      const totalProjects = projectsList.length;
      const activeProjects = projectsList.filter((p: Project) => 
        p.attributes.status === 'in_progress' || p.attributes.status === 'planning'
      ).length;
      const completedProjects = projectsList.filter((p: Project) => 
        p.attributes.status === 'completed'
      ).length;
      const totalDamages = alertsData.meta?.pagination?.total ?? alertsData.data?.length ?? 0;

      setStats({
        totalProjects,
        activeProjects,
        totalDamages,
        completedProjects
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'in_progress': return '#2196F3';
      case 'planning': return '#FF9800';
      case 'paused': return '#9E9E9E';
      default: return '#666';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('completed');
      case 'in_progress': return t('inProgress');
      case 'planning': return t('planning');
      case 'paused': return t('paused');
      default: return status;
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.attributes.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.attributes.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <div className="loading">{t('loading')}</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>{t('dashboard')}</h1>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{stats.totalProjects}</h3>
          <p>{t('totalProjects')}</p>
        </div>
        <div className="stat-card">
          <h3>{stats.activeProjects}</h3>
          <p>{t('activeProjects')}</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalDamages}</h3>
          <p>{t('totalDamages')}</p>
        </div>
        <div className="stat-card">
          <h3>{stats.completedProjects}</h3>
          <p>{t('completedProjects')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="dashboard-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('searchProjects')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="all">{t('allStatuses')}</option>
          <option value="planning">{t('planning')}</option>
          <option value="in_progress">{t('inProgress')}</option>
          <option value="completed">{t('completed')}</option>
          <option value="paused">{t('paused')}</option>
        </select>
      </div>

      {/* Projects Grid */}
      <div className="projects-grid">
        {filteredProjects.map((project) => (
          <Link 
            key={project.id} 
            to={`/project/${project.id}`} 
            className="project-card"
          >
            <div className="project-image">
              {project.attributes.image?.data?.attributes.url ? (
                <img 
                  src={`${sessionStorage.getItem('backendUrl')}${project.attributes.image.data.attributes.url}`} 
                  alt={project.attributes.name}
                />
              ) : (
                <div className="placeholder-image">
                  <span>{project.attributes.name.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>
            
            <div className="project-info">
              <h3>{project.attributes.name}</h3>
              <p className="project-description">
                {project.attributes.description || t('noDescription')}
              </p>
              
              <div className="project-meta">
                <span 
                  className="status-badge" 
                  style={{ backgroundColor: getStatusColor(project.attributes.status) }}
                >
                  {getStatusText(project.attributes.status)}
                </span>
                
                {project.attributes.location && (
                  <span className="location">📍 {project.attributes.location}</span>
                )}
              </div>
              
              <div className="project-stats">
                <span>
                  {project.attributes.alerts?.data?.filter(
                    (alert: any) => alert.attributes?.roadDamageNumber
                  ).length || 0} {t('damages')}
                </span>
                <span>{project.attributes.workReports?.data?.length || 0} {t('workReports')}</span>
              </div>
              
              {project.attributes.client && (
                <p className="client">{t('client')}: {project.attributes.client}</p>
              )}
            </div>
          </Link>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="no-projects">
          <h3>{t('noProjectsFound')}</h3>
          <p>{t('noProjectsDescription')}</p>
        </div>
      )}
    </div>
  );
};